import type { TransformedMissionData, TransformedMissionTask, WeeklyMissionResponse } from '@tryhackme/interfaces';
import { RTKQueryTag } from 'src/common/constants';
import type { ApiResponse } from 'src/common/interfaces';
import type { EmploymentJourneyProgress } from 'src/common/interfaces/employment-journey';
import { apiSlice } from 'src/features/api/api-slice';
import type { CurrentModuleResponse } from './dashboard.types';
const transformMissionResponse = (response: ApiResponse<WeeklyMissionResponse>): ApiResponse<TransformedMissionData | null> => {
  if (!response.data) {
    return {
      ...response,
      data: null
    };
  }
  const mission = response.data;
  const transformedTasks: TransformedMissionTask[] = Object.entries(mission.tasks).map(([taskType, goalValue]) => {
    const typedTaskType = taskType as keyof typeof mission.tasks;
    const progressValue = mission.progress[typedTaskType];
    return {
      type: typedTaskType,
      count: typeof goalValue === 'boolean' ? 1 : goalValue,
      countCompleted: typeof progressValue === 'boolean' ? progressValue ? 1 : 0 : progressValue || 0
    };
  });
  const transformedData: TransformedMissionData = {
    type: mission.type,
    tasks: transformedTasks,
    status: mission.status,
    created: new Date(mission.created),
    completedAt: mission.completedAt ? new Date(mission.completedAt) : null,
    reward: mission.reward,
    weekEnd: new Date(mission.weekEnd),
    weekStart: new Date(mission.weekStart)
  };
  return {
    ...response,
    data: transformedData
  };
};
export const extendedApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    getCurrentModule: builder.query<ApiResponse<CurrentModuleResponse>, void>({
      query: () => ({
        method: 'GET',
        url: 'paths/current-module'
      }),
      providesTags: [RTKQueryTag.PathsCurrentModule]
    }),
    offerStreakRepair: builder.mutation<ApiResponse, void>({
      query: () => ({
        method: 'POST',
        url: 'users/offer-streak-repair'
      })
    }),
    getWeeklyMission: builder.query<ApiResponse<TransformedMissionData | null>, void>({
      query: () => ({
        method: 'GET',
        url: 'users/missions/weekly'
      }),
      transformResponse: transformMissionResponse,
      providesTags: [RTKQueryTag.WeeklyMission]
    }),
    getEmploymentJourneyProgress: builder.query<ApiResponse<EmploymentJourneyProgress>, void>({
      query: () => ({
        method: 'GET',
        url: 'users/employment-journey/progress'
      }),
      providesTags: [RTKQueryTag.EmploymentJourneyProgress]
    })
  })
});
export const {
  useGetCurrentModuleQuery,
  useOfferStreakRepairMutation,
  useGetWeeklyMissionQuery,
  useGetEmploymentJourneyProgressQuery
} = extendedApiSlice;